import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { RedirectError } from 'src/shared/error';

import { differenceInDays } from 'src/app/shared/utils/dates';

import type { GammaPetitionDetailsDataQuery, GammaPetitionDetailsDataQueryVariables } from './api.graphql';
import { GammaPetitionDetailsData } from './api.graphql';
import { getContactDmConfig } from './getContactDmConfig';
import { getLegislativeBodiesConfig } from './getLegislativeBodiesConfig';
import { getPetitionStrengthScore } from './getPetitionStrengthScore';
import { getSupporterVoicesVideos } from './supporterVoicesVideos';
import type { PetitionDetailsPageData } from './types';
import { getPetitionShareVisuals } from './utilities/getPetitionShareVisuals';

async function fetchPetitionDetails(utilityContext: UtilityContext, variables: GammaPetitionDetailsDataQueryVariables) {
	const {
		gql: { fetch },
	} = utilityContext;
	const { data } = await fetch<GammaPetitionDetailsDataQuery, GammaPetitionDetailsDataQueryVariables>({
		query: GammaPetitionDetailsData,
		variables,
		rejectOnError: true,
		// to separate query from other batched queries
		batched: false,
	});
	return data;
}

function normalizePetition(
	petition: NonNullable<GammaPetitionDetailsDataQuery['petition']>,
): PetitionDetailsPageData['petition'] {
	return {
		...petition,
		primaryDecisionMakerConnection: {
			totalCount: petition.primaryDecisionMakerConnection.totalCount,
			nodes: petition.primaryDecisionMakerConnection.edges.map((edge) => ({
				...edge.node,
				targetPetitionAssociationData: edge.associationData,
			})),
		},
		shareVisuals: getPetitionShareVisuals(petition),
	};
}

export async function getPetitionDetailsPageData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		locale,
		countryCode,
		loadBandits,
	}: {
		locale: string;
		countryCode: string;
		loadBandits: boolean;
	},
): Promise<PetitionDetailsPageData> {
	const { emailEnabled, emailBody, emailSubjectLine } = await getContactDmConfig(utilityContext);
	const legislativeBodiesEnabled = await getLegislativeBodiesConfig(utilityContext);
	const numDmsToFetch = legislativeBodiesEnabled ? null : 5;

	const data = await fetchPetitionDetails(utilityContext, {
		slugOrId,
		locale,
		countryCode,
		loadBandits,
		commentsSortBy: 'SCORE',
		emailEnabled,
		numDmsToFetch,
	});

	if (!data.petition) throw new RedirectError({ url: '/?petition_not_found=true' });

	const supporterVoicesVideos = await getSupporterVoicesVideos(
		utilityContext,
		slugOrId,
		!!data.petition.videoSettingsMetadata.disableDisplay,
	);

	const petition = normalizePetition(data.petition);

	return {
		petition,
		recommendedDecisionMakers: data.recommendedDecisionMakers?.items || [],
		createdLessThanOneDayAgo: differenceInDays(new Date(), new Date(data.petition.createdAt)) < 1,
		score: getPetitionStrengthScore(data.petition),
		supporterVoicesVideos,
		emailDm: { emailSubjectLine, emailBody },
		mediaHitsPage: {
			isLastPage: !petition.mediaHitsConnection.pageInfo.hasNextPage,
			numberOfStarterMedia: petition.mediaHitsConnection.totalCount,
			mediaHits: petition.mediaHitsConnection.nodes,
		},
		featureConfigs: data.featureConfigs,
	};
}
